import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem,
  Paper, Select, Slider, Snackbar, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ScienceIcon from '@mui/icons-material/Science';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';
import api from '../../api';
import QuestionDisplay from '../../components/QuestionDisplay';
import PracticalInstructions from '../../components/PracticalInstructions';
import { getFood, listFoods, type FoodComponent, type FoodId } from './foodDatabase';
import FoodTestApparatus from './FoodTestApparatus';
import FoodTestNotebook from './FoodTestNotebook';
import FoodTestVisuals, { type FoodObservation } from './FoodTestVisuals';
import { generateFoodTestReport } from './ReportGenerator';
import StepGuide, { getFoodTestFlow } from './StepGuide';
import { getTestOutcome } from './tests';
import { assessSafetyAction } from './safety';
import type { FoodTestId, ProcedureAction, ProcedureState, ProcedureStep } from './types';
import './foodTestsLab.css';

type FoodLabMode = 'identify' | 'guided' | 'compare' | 'semi-quantitative' | 'safety' | 'sandbox';

interface SavedSession {
  _id: string;
  title: string;
  updatedAt?: string;
  state?: Partial<FoodLabState>;
}

interface SavedReport {
  _id: string;
  sessionId: string;
  title: string;
  fileName: string;
  createdAt: string;
}

interface FoodLabState {
  mode: FoodLabMode;
  foodId: FoodId;
  unknownFoodId: FoodId;
  test: FoodTestId;
  procedure: ProcedureState;
  observations: FoodObservation[];
  conclusion: string;
  compositionGuess: FoodComponent[];
  safetyMode: boolean;
}

const unknownFoodIds: FoodId[] = ['glucose', 'sucrose', 'starch-solution', 'egg-white', 'milk', 'vegetable-oil', 'orange-juice', 'water', 'potato', 'bread'];
const tests: Array<{ id: FoodTestId; label: string; reagent: string }> = [
  { id: 'reducing-sugar', label: 'Reducing sugars', reagent: 'Benedict’s solution' },
  { id: 'non-reducing-sugar', label: 'Non-reducing sugars', reagent: 'Dilute HCl, NaHCO₃, Benedict’s' },
  { id: 'starch', label: 'Starch', reagent: 'Iodine solution' },
  { id: 'protein', label: 'Protein', reagent: 'NaOH, then CuSO₄' },
  { id: 'lipid', label: 'Lipids', reagent: 'Ethanol, then distilled water' },
  { id: 'vitamin-c', label: 'Vitamin C', reagent: 'DCPIP solution' },
];
const componentLabels: Record<FoodComponent, string> = {
  reducingSugar: 'Reducing sugar', nonReducingSugar: 'Non-reducing sugar', starch: 'Starch',
  protein: 'Protein', lipid: 'Lipid', vitaminC: 'Vitamin C',
};
const blankProcedure: ProcedureState = { steps: [], sampleVolumeMl: 0, reagentVolumesMl: {}, waterBathTempC: 96, dcpipDrops: 0 };
const modeLabels: Record<FoodLabMode, string> = {
  identify: 'Identify the unknown', guided: 'Learn each test', compare: 'Compare foods',
  'semi-quantitative': 'Concentration practice', safety: 'Technique and safety', sandbox: 'Free play',
};

const FoodTestsLab: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { openLogin } = useAuthModal();
  const [mode, setMode] = useState<FoodLabMode>('identify');
  const [foodId, setFoodId] = useState<FoodId>('glucose');
  const [unknownFoodId, setUnknownFoodId] = useState<FoodId>(() => unknownFoodIds[Math.floor(Math.random() * unknownFoodIds.length)]);
  const [test, setTest] = useState<FoodTestId>('reducing-sugar');
  const [procedure, setProcedure] = useState<ProcedureState>(blankProcedure);
  const [observations, setObservations] = useState<FoodObservation[]>([]);
  const [conclusion, setConclusion] = useState('');
  const [compositionGuess, setCompositionGuess] = useState<FoodComponent[]>([]);
  const [safetyMode, setSafetyMode] = useState(true);
  const [darkTheme, setDarkTheme] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [savedSessions, setSavedSessions] = useState<SavedSession[]>([]);
  const [savedReports, setSavedReports] = useState<SavedReport[]>([]);
  const [selectedReportId, setSelectedReportId] = useState('');
  const [sessionReady, setSessionReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sessionMessage, setSessionMessage] = useState('Preparing a private lab session…');
  const [notice, setNotice] = useState('');
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [isHeating, setIsHeating] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [identificationSubmitted, setIdentificationSubmitted] = useState(false);
  const [identificationScore, setIdentificationScore] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const autosaveTimer = useRef<number | null>(null);
  const countdownTimer = useRef<number | null>(null);
  const sessionInitStarted = useRef(false);
  const currentStateRef = useRef<FoodLabState | null>(null);

  const activeFoodId = mode === 'identify' ? unknownFoodId : foodId;
  const activeFood = getFood(activeFoodId);
  const visibleSampleName = mode === 'identify' && !identificationSubmitted ? 'Unknown food sample' : activeFood.name;
  const outcome = useMemo(() => getTestOutcome(activeFoodId, test, procedure), [activeFoodId, test, procedure]);
  const activeTest = tests.find((item) => item.id === test) || tests[0];

  useEffect(() => {
    setShowOnboarding(localStorage.getItem('food-tests-onboarded') !== 'true');
  }, []);

  const persistableState = useCallback((): FoodLabState => ({
    mode, foodId, unknownFoodId, test, procedure, observations, conclusion, compositionGuess, safetyMode,
  }), [mode, foodId, unknownFoodId, test, procedure, observations, conclusion, compositionGuess, safetyMode]);
  currentStateRef.current = persistableState();

  const restoreSession = useCallback((saved: SavedSession & { state?: Partial<FoodLabState> }) => {
    const state = saved.state || {};
    setSessionId(saved._id);
    setMode(state.mode || 'identify');
    setFoodId(state.foodId || 'glucose');
    setUnknownFoodId(state.unknownFoodId || unknownFoodIds[0]);
    setTest(state.test || 'reducing-sugar');
    setProcedure(state.procedure || blankProcedure);
    setObservations(state.observations || []);
    setConclusion(state.conclusion || '');
    setCompositionGuess(state.compositionGuess || []);
    setSafetyMode(state.safetyMode ?? true);
    setSessionReady(true);
    setIdentificationSubmitted(false);
    setIdentificationScore(null);
    setSessionMessage('Session autosaves to your account.');
  }, []);

  const initializeSession = useCallback(async () => {
    if (!isAuthenticated || sessionInitStarted.current) return;
    sessionInitStarted.current = true;
    setSessionReady(false);
    try {
      const response = await api.get('/food-tests/session');
      const sessions: SavedSession[] = response.data?.data || [];
      setSavedSessions(sessions);
      const reportsResponse = await api.get('/food-tests/reports');
      setSavedReports(reportsResponse.data?.data || []);
      if (sessions.length) {
        const detail = await api.get(`/food-tests/session/${sessions[0]._id}`);
        restoreSession(detail.data.data);
      } else {
        const created = await api.post('/food-tests/session', { title: 'Food tests practical', state: currentStateRef.current });
        restoreSession(created.data.data);
        setSavedSessions([created.data.data]);
      }
    } catch (error: any) {
      setSessionMessage(error?.response?.data?.message || 'Session could not be loaded. Work remains available in this browser.');
      setSessionReady(true);
    }
  }, [isAuthenticated, restoreSession]);

  useEffect(() => { void initializeSession(); }, [initializeSession]);

  useEffect(() => {
    if (!sessionReady || !sessionId || !isAuthenticated) return undefined;
    if (autosaveTimer.current !== null) window.clearTimeout(autosaveTimer.current);
    setSaving(true);
    autosaveTimer.current = window.setTimeout(async () => {
      try {
        const response = await api.patch(`/food-tests/session/${sessionId}`, { state: persistableState() });
        setSavedSessions((current) => current.map((item) => item._id === sessionId ? { ...item, updatedAt: response.data.data.updatedAt } : item));
        setSessionMessage('Observations and apparatus state are saved to your account.');
      } catch (error: any) {
        setSessionMessage(error?.response?.data?.message || 'Autosave failed. Your work remains in this browser.');
      } finally { setSaving(false); }
    }, 850);
    return () => { if (autosaveTimer.current !== null) window.clearTimeout(autosaveTimer.current); };
  }, [sessionReady, sessionId, isAuthenticated, persistableState]);

  useEffect(() => () => {
    if (countdownTimer.current !== null) window.clearInterval(countdownTimer.current);
  }, []);

  const resumeSession = async (id: string) => {
    try {
      setSessionReady(false);
      const response = await api.get(`/food-tests/session/${id}`);
      restoreSession(response.data.data);
      setSessionMessage('Previous food-test session resumed.');
    } catch (error: any) {
      setSessionReady(true);
      setNotice(error?.response?.data?.message || 'Could not resume that session.');
    }
  };

  const createSession = async () => {
    try {
      setSessionReady(false);
      const response = await api.post('/food-tests/session', { title: 'Food tests practical', state: persistableState() });
      restoreSession(response.data.data);
      setSavedSessions((current) => [response.data.data, ...current]);
      setObservations([]);
      setConclusion('');
      setProcedure(blankProcedure);
    } catch (error: any) {
      setSessionReady(true);
      setNotice(error?.response?.data?.message || 'Could not create a new session.');
    }
  };

  const resetProcedure = () => {
    if (countdownTimer.current !== null) window.clearInterval(countdownTimer.current);
    setProcedure(blankProcedure);
    setTimerSeconds(0);
    setIsHeating(false);
    setNotice('The current tube has been washed. Its procedure has been reset.');
  };

  const completeAction = (action: ProcedureAction) => {
    setProcedure((current) => {
      const steps: ProcedureStep[] = [...current.steps, { action, timestamp: new Date().toISOString() }];
      const reagentVolumesMl = { ...current.reagentVolumesMl };
      if (action === 'add-benedicts') reagentVolumesMl.benedicts = (reagentVolumesMl.benedicts || 0) + 2;
      if (action === 'add-dilute-hcl') reagentVolumesMl['dilute-hcl'] = (reagentVolumesMl['dilute-hcl'] || 0) + 1;
      if (action === 'neutralise-nahco3') reagentVolumesMl['sodium-hydrogen-carbonate'] = (reagentVolumesMl['sodium-hydrogen-carbonate'] || 0) + 1;
      if (action === 'add-iodine') reagentVolumesMl.iodine = (reagentVolumesMl.iodine || 0) + 0.2;
      if (action === 'add-sodium-hydroxide') reagentVolumesMl['sodium-hydroxide'] = (reagentVolumesMl['sodium-hydroxide'] || 0) + 1;
      if (action === 'add-copper-sulfate') reagentVolumesMl['copper-sulfate'] = (reagentVolumesMl['copper-sulfate'] || 0) + 0.2;
      if (action === 'add-ethanol') reagentVolumesMl.ethanol = (reagentVolumesMl.ethanol || 0) + 2;
      if (action === 'add-water') reagentVolumesMl['distilled-water'] = (reagentVolumesMl['distilled-water'] || 0) + 2;
      if (action === 'add-dcpip') reagentVolumesMl.dcpip = (reagentVolumesMl.dcpip || 0) + 1;
      return {
        ...current,
        steps,
        sampleVolumeMl: action === 'add-sample' ? (current.sampleVolumeMl || 0) + 2 : current.sampleVolumeMl,
        reagentVolumesMl,
        dcpipDrops: action === 'add-dcpip' ? (current.dcpipDrops || 0) + 1 : current.dcpipDrops,
        pH: action === 'neutralise-nahco3' ? 7 : current.pH,
      };
    });
    if (action === 'heat-water-bath') setIsHeating(true);
    if (action === 'check-neutral-ph') setNotice('Indicator check: approximately neutral (pH 7).');
  };

  const runTimer = () => {
    if (timerSeconds > 0) return;
    setTimerSeconds(5);
    countdownTimer.current = window.setInterval(() => {
      setTimerSeconds((remaining) => {
        if (remaining <= 1) {
          if (countdownTimer.current !== null) window.clearInterval(countdownTimer.current);
          countdownTimer.current = null;
          setIsHeating(false);
          completeAction('wait-five-minutes');
          return 0;
        }
        return remaining - 1;
      });
    }, 1000);
  };

  const advanceProcedure = (action: ProcedureAction) => {
    if (action === 'wait-five-minutes') { runTimer(); return; }
    if (action === 'add-sodium-hydroxide' || action === 'add-copper-sulfate') {
      const warning = assessSafetyAction(action).warnings[0];
      setNotice(warning);
    }
    completeAction(action);
  };

  const attemptUnsafeAction = () => {
    const assessment = assessSafetyAction('heat-direct-flame');
    setNotice(assessment.warnings[0]);
  };

  const downloadSavedReport = async () => {
    if (!selectedReportId) return;
    try {
      const response = await api.get(`/food-tests/report/${selectedReportId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'virtual-food-tests-report.pdf';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      setNotice(error?.response?.data?.message || 'Could not download the saved report.');
    }
  };

  const handleTestChange = (nextTest: FoodTestId) => {
    setTest(nextTest);
    setProcedure(blankProcedure);
    setTimerSeconds(0);
    setIsHeating(false);
  };

  const recordObservation = () => {
    if (outcome.category === 'failed') { setNotice(outcome.observation); return; }
    const existing = observations.find((item) => item.foodId === activeFoodId && item.test === test);
    const observation: FoodObservation = {
      id: existing?.id || `food-observation-${Date.now()}`,
      foodId: activeFoodId,
      foodName: visibleSampleName,
      test,
      category: outcome.category,
      observation: outcome.observation,
      inference: existing?.inference || '',
      colourName: outcome.colourName,
      dropsAdded: outcome.dropsAdded,
      dropsRequired: outcome.dropsRequired,
      timestamp: new Date().toISOString(),
    };
    setObservations((current) => [observation, ...current.filter((item) => !(item.foodId === activeFoodId && item.test === test))]);
    setNotice('Observation recorded in the laboratory notebook.');
  };

  const setComposition = (component: FoodComponent, checked: boolean) => {
    setCompositionGuess((current) => checked ? [...new Set([...current, component])] : current.filter((item) => item !== component));
  };

  const submitIdentification = () => {
    const trueComponents = (Object.keys(componentLabels) as FoodComponent[])
      .filter((component) => activeFood.composition[component] >= 0.1);
    const correct = trueComponents.filter((component) => compositionGuess.includes(component)).length;
    const falsePositives = compositionGuess.filter((component) => !trueComponents.includes(component)).length;
    const score = Math.round(Math.max(0, (correct - falsePositives) / Math.max(1, trueComponents.length) * 100));
    setIdentificationSubmitted(true);
    setIdentificationScore(score);
    setShowResult(true);
    setNotice(`Composition identification recorded: ${score}% evidence match.`);
  };

  const exportReport = async () => {
    const reportBlob = generateFoodTestReport({
      studentName: user?.name || 'Student',
      observations,
      conclusion,
      unknownFoodName: mode === 'identify' && identificationSubmitted ? activeFood.name : undefined,
    });
    if (!sessionId) return;
    const formData = new FormData();
    formData.append('sessionId', sessionId);
    formData.append('title', 'Virtual Food Tests Laboratory Report');
    formData.append('conclusion', conclusion);
    formData.append('observations', JSON.stringify(observations));
    formData.append('report', reportBlob, 'virtual-food-tests-report.pdf');
    try {
      await api.post('/food-tests/report', formData);
      const reportsResponse = await api.get('/food-tests/reports');
      setSavedReports(reportsResponse.data?.data || []);
      setNotice('PDF downloaded and report saved to your account.');
    } catch (error: any) {
      setNotice(error?.response?.data?.message || 'PDF downloaded; account copy could not be saved.');
    }
  };

  const changeFood = (nextFood: FoodId) => {
    setFoodId(nextFood);
    setProcedure(blankProcedure);
    setCompositionGuess([]);
    setIdentificationSubmitted(false);
  };

  const changeMode = (nextMode: FoodLabMode) => {
    setMode(nextMode);
    setProcedure(blankProcedure);
    setCompositionGuess([]);
    setIdentificationSubmitted(false);
    if (nextMode === 'identify') setUnknownFoodId((current) => unknownFoodIds[(unknownFoodIds.indexOf(current) + 1) % unknownFoodIds.length]);
  };

  if (authLoading) return <Container sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Container>;
  if (!isAuthenticated) return <Container maxWidth="sm" sx={{ py: 8 }}><Alert severity="info" action={<Button color="inherit" onClick={() => openLogin()}>Sign in</Button>}>Sign in to use the Virtual Food Tests Laboratory.</Alert></Container>;

  return (
    <Box className={darkTheme ? 'food-lab food-lab-dark' : 'food-lab'}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title="Back to lab list"><IconButton onClick={() => navigate('/labs')} aria-label="Back to labs"><ArrowBackIcon /></IconButton></Tooltip>
            <Box><Typography variant="h4" fontWeight={900}>Virtual Food Tests Laboratory</Typography><Typography color="text.secondary">Prepare samples, follow the method, observe reactions, and build your evidence table.</Typography></Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip size="small" color={saving ? 'default' : sessionId ? 'success' : 'warning'} label={saving ? 'Saving…' : sessionId ? 'Autosaved' : 'Local session'} />
            <Tooltip title="Switch laboratory theme"><IconButton onClick={() => setDarkTheme((current) => !current)} aria-label="Toggle dark theme"><Brightness4Icon /></IconButton></Tooltip>
            <Button size="small" startIcon={<RestartAltIcon />} onClick={resetProcedure}>Wash / reset tube</Button>
          </Stack>
        </Stack>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 225 }}>
              <InputLabel id="food-mode-label">Practical mode</InputLabel>
              <Select labelId="food-mode-label" label="Practical mode" value={mode} onChange={(event) => changeMode(event.target.value as FoodLabMode)}>
                {(Object.entries(modeLabels) as Array<[FoodLabMode, string]>).map(([id, label]) => <MenuItem key={id} value={id}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            {mode !== 'identify' && <FormControl size="small" sx={{ minWidth: 210 }}>
              <InputLabel id="food-sample-label">Food sample</InputLabel>
              <Select labelId="food-sample-label" label="Food sample" value={foodId} onChange={(event) => changeFood(event.target.value as FoodId)}>
                {listFoods().map((food) => <MenuItem key={food.id} value={food.id}>{food.name}</MenuItem>)}
              </Select>
            </FormControl>}
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="food-test-label">Food test</InputLabel>
              <Select labelId="food-test-label" label="Food test" value={test} onChange={(event) => handleTestChange(event.target.value as FoodTestId)}>
                {tests.map((item) => <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 230 }}>
              <InputLabel id="food-session-label">Saved sessions</InputLabel>
              <Select labelId="food-session-label" label="Saved sessions" value={sessionId || ''} onChange={(event) => void resumeSession(String(event.target.value))}>
                <MenuItem value="" disabled>Select a saved session</MenuItem>
                {savedSessions.map((session) => <MenuItem key={session._id} value={session._id}>{session.title} · {session.updatedAt ? new Date(session.updatedAt).toLocaleDateString() : 'new'}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={() => void createSession()}>New session</Button>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mt: 1.25 }}>
            <Typography variant="caption" color="text.secondary">{sessionMessage}</Typography>
            <FormControlLabel control={<Checkbox checked={safetyMode} onChange={(event) => setSafetyMode(event.target.checked)} size="small" />} label={<Typography variant="body2">Safety mode</Typography>} />
          </Stack>
        </Paper>

        <QuestionDisplay labMode="practice" questionType="food-tests" />
        <PracticalInstructions
          title="Food tests: complete practical instructions"
          introduction="Use a clean tube or tile for each test, take a fresh portion of food, and record the reagent, order, condition, colour, and inference. For solid foods, grind a small portion with distilled water for tube tests; use a fresh cut surface or crumb on a white tile for iodine."
          sections={[
            { title: 'A. Reducing sugars - Benedict’s test', steps: [
              'Place about 2 cm³ of prepared food sample in a clean test tube.',
              'Add about 2 cm³ of blue Benedict’s solution and mix gently. Use approximately equal volumes for comparisons.',
              'Place the tube in a near-boiling water bath for about five minutes. Do not heat Benedict’s solution directly over a flame.',
              'Record the final colour: blue is negative; green indicates trace; yellow low; orange moderate; brick-red precipitate high reducing sugar.',
              'Compare colour intensity only when sample and reagent volumes, bath temperature, and heating time are similar.'
            ] },
            { title: 'B. Non-reducing sugars - hydrolysis then Benedict’s', steps: [
              'Add about 2 cm³ of sample to a clean tube and add dilute hydrochloric acid.',
              'Heat the acidic mixture in a near-boiling water bath for about five minutes to hydrolyse sucrose into reducing sugars.',
              'Remove the tube with a holder and allow it to cool. Add sodium hydrogen carbonate carefully in small portions to neutralise the acid.',
              'Check a drop with pH paper. Continue careful neutralisation until approximately neutral (about pH 6–8); do not add Benedict’s while the sample is acidic.',
              'Add an approximately equal volume of Benedict’s solution and heat again in the water bath for about five minutes.',
              'Record the Benedict’s colour result. Sucrose should test positive after successful hydrolysis and neutralisation.'
            ] },
            { title: 'C. Starch - iodine test', steps: [
              'Place a small liquid sample in a clean tube, or place a small ground solid sample on a white tile.',
              'Add a few drops of yellow-brown iodine solution at room temperature. Do not heat.',
              'Record blue-black as starch present; if iodine stays yellow-brown, starch was not detected.'
            ] },
            { title: 'D. Protein - Biuret test', steps: [
              'Place about 2 cm³ of prepared sample in a clean test tube.',
              'Add sodium hydroxide solution first to make the mixture alkaline and mix carefully.',
              'Add a few drops of copper(II) sulfate solution and mix gently.',
              'Record violet/purple or lilac as protein present. If it remains blue, protein was not detected. No heating is required.'
            ] },
            { title: 'E. Lipids - emulsion test', steps: [
              'Place a small food sample in a clean, dry test tube.',
              'Add about 2 cm³ ethanol and shake to dissolve any lipid.',
              'Add the ethanolic extract to water, or add distilled water carefully to the tube, only after the ethanol step.',
              'Record a cloudy, milky-white emulsion as lipid present; a clear mixture is negative. Keep ethanol away from flames.'
            ] },
            { title: 'F. Vitamin C - DCPIP test', steps: [
              'Add a fixed measured volume of blue DCPIP solution to a clean tube.',
              'Add the food sample one drop at a time, shaking gently after each drop.',
              'Count and record the drops needed for the blue colour to disappear. Fewer drops indicate a higher relative vitamin C concentration.',
              'Use the same DCPIP volume and drop size when comparing fresh juice, heated juice, and the water control.'
            ] },
            { title: 'G. Record and interpret', steps: [
              'Use Auto-record after a valid procedure, then edit the observation if needed and enter an evidence-based inference.',
              'Test a water negative control and appropriate positive controls when available. Compare multiple tests before identifying an unknown.',
              'Use the results matrix to compare samples, enter a conclusion, and export the notebook as CSV or PDF.'
            ] }
          ]}
          safety={[
            'Wear eye protection and handle sodium hydroxide as corrosive. Avoid contact with copper(II) sulfate and follow proper waste disposal.',
            'Use a test-tube holder for hot glass. Heat tubes in a water bath, never directly in a flame, and never heat a sealed tube.',
            'Keep ethanol away from the Bunsen burner and other ignition sources. Switch off flames before the emulsion test.',
            'Add sodium hydrogen carbonate slowly after acid hydrolysis because neutralisation releases gas and can cause foaming.'
          ]}
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.45fr) minmax(330px, .85fr)' }, gap: 2, mt: 2 }}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
                <Box><Typography variant="subtitle1" fontWeight={800}>{activeTest.label}</Typography><Typography variant="body2" color="text.secondary">Reagents: {activeTest.reagent}</Typography></Box>
                <Chip icon={<ScienceIcon />} label={`Sample: ${visibleSampleName}`} />
              </Stack>
            </Paper>
            <FoodTestApparatus
              sampleLabel={visibleSampleName}
              test={test}
              outcome={outcome}
              isHeating={isHeating}
              onWash={resetProcedure}
              actions={getFoodTestFlow(test).map((step) => ({ action: step.action, label: step.label }))}
              onAction={advanceProcedure}
            />
            {test === 'reducing-sugar' || test === 'non-reducing-sugar' ? <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Typography variant="caption">Water-bath temperature: {procedure.waterBathTempC ?? 96} °C</Typography>
              <Slider aria-label="Water bath temperature" min={40} max={100} step={1} value={procedure.waterBathTempC ?? 96} onChange={(_, value) => setProcedure((current) => ({ ...current, waterBathTempC: Array.isArray(value) ? value[0] : value }))} />
              <Stack direction="row" spacing={2}>
                <TextField size="small" type="number" label="Sample volume (cm³)" inputProps={{ min: 0, step: 0.5 }} value={procedure.sampleVolumeMl || 0} onChange={(event) => setProcedure((current) => ({ ...current, sampleVolumeMl: Number(event.target.value) }))} />
                <TextField size="small" type="number" label="Benedict’s volume (cm³)" inputProps={{ min: 0, step: 0.5 }} value={procedure.reagentVolumesMl?.benedicts || 0} onChange={(event) => setProcedure((current) => ({ ...current, reagentVolumesMl: { ...current.reagentVolumesMl, benedicts: Number(event.target.value) } }))} />
              </Stack>
            </Paper> : null}
            {mode === 'sandbox' ? <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight={800}>Reagent and apparatus actions</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                {(['add-sample', 'add-benedicts', 'add-iodine', 'add-sodium-hydroxide', 'add-copper-sulfate', 'add-dilute-hcl', 'neutralise-nahco3', 'add-ethanol', 'add-water', 'add-dcpip', 'add-food-drop', 'shake', 'heat-water-bath', 'heat-direct-flame'] as ProcedureAction[]).map((action) => <Button key={action} size="small" variant="outlined" onClick={() => action === 'heat-direct-flame' ? attemptUnsafeAction() : advanceProcedure(action)}>{action.replaceAll('-', ' ')}</Button>)}
              </Stack>
            </Paper> : null}
            <StepGuide test={test} steps={procedure.steps} onAdvance={advanceProcedure} runningTimer={timerSeconds > 0} timerSeconds={timerSeconds} />
            {outcome.category !== 'failed' && procedure.steps.length > 0 && <Alert severity={outcome.category === 'negative' ? 'info' : 'success'} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <Typography variant="body2" fontWeight={800}>{outcome.category.replace('-', ' ')} · {outcome.colourName}</Typography>
              <Typography variant="body2">{outcome.observation}</Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{outcome.reasoning}</Typography>
              {outcome.warnings.map((warning) => <Typography key={warning} variant="caption" display="block" color="warning.dark">{warning}</Typography>)}
            </Alert>}
            {test === 'vitamin-c' && procedure.dcpipDrops && procedure.dcpipDrops > 0 && <Button variant="outlined" onClick={() => { completeAction('add-food-drop'); completeAction('shake'); }} disabled={outcome.colourName === 'colourless'}>Add next food drop and shake</Button>}
            {safetyMode && <Alert severity="warning">Use eye protection with sodium hydroxide and copper(II) sulfate. Heat Benedict’s only in the water bath; never heat a sealed tube.</Alert>}
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button variant="contained" onClick={recordObservation} disabled={outcome.category === 'failed'}>Record observation</Button>
              {safetyMode && <Button variant="outlined" color="warning" onClick={attemptUnsafeAction}>Try direct-flame heating</Button>}
            </Stack>
            {mode === 'identify' && <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle1" fontWeight={800}>Infer the food composition</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Select the components supported by your recorded tests.</Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {(Object.keys(componentLabels) as FoodComponent[]).map((component) => <FormControlLabel key={component} control={<Checkbox checked={compositionGuess.includes(component)} onChange={(event) => setComposition(component, event.target.checked)} />} label={componentLabels[component]} />)}
              </Stack>
              <Button variant="contained" sx={{ mt: 1 }} onClick={submitIdentification} disabled={!observations.some((item) => item.foodId === activeFoodId)}>Submit composition inference</Button>
              {identificationScore !== null && <Chip sx={{ mt: 1, ml: 1 }} color="success" label={`Evidence match: ${identificationScore}%`} />}
              {identificationSubmitted && <Alert severity="success" sx={{ mt: 1 }}>Unknown sample: {activeFood.name}. Profile: {(Object.keys(componentLabels) as FoodComponent[]).filter((component) => activeFood.composition[component] >= 0.1).map((component) => componentLabels[component]).join(', ') || 'none detected'}.</Alert>}
            </Paper>}
            {mode !== 'identify' && <TextField label="Conclusion and discussion" multiline minRows={3} value={conclusion} onChange={(event) => setConclusion(event.target.value)} fullWidth />}
          </Stack>
          <Stack spacing={2}>
            <FoodTestVisuals observations={observations} currentOutcome={outcome} />
            {mode === 'compare' && <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight={800}>Comparison set</Typography>
              <Typography variant="body2" color="text.secondary">Choose a different food sample above, run the same test, and record it to add rows to the matrix.</Typography>
            </Paper>}
            {mode === 'semi-quantitative' && <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight={800}>Semi-quantitative reading</Typography>
              {test === 'vitamin-c' ? <Typography variant="body2">DCPIP decolourises after {outcome.dropsAdded ?? 0} food drops; fewer drops indicate more vitamin C.</Typography> : <Typography variant="body2">Benedict’s ladder position: {outcome.colourName}. Use equal sample and reagent volumes for comparison.</Typography>}
            </Paper>}
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FoodTestNotebook observations={observations} onChange={setObservations} onExportPdf={exportReport} onAutoRecord={recordObservation} onAddManual={() => setObservations((current) => [{ id: `manual-${Date.now()}`, foodId: activeFoodId, foodName: visibleSampleName, test, category: 'unrecorded', observation: '', inference: '', colourName: 'not recorded', timestamp: new Date().toISOString() }, ...current])} />
          {savedReports.length > 0 && <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, borderRadius: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 280, flex: 1 }}>
                <InputLabel id="saved-food-report-label">Saved PDF reports</InputLabel>
                <Select labelId="saved-food-report-label" label="Saved PDF reports" value={selectedReportId} onChange={(event) => setSelectedReportId(String(event.target.value))}>
                  {savedReports.map((report) => <MenuItem key={report._id} value={report._id}>{report.title} · {new Date(report.createdAt).toLocaleDateString()}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={() => void downloadSavedReport()} disabled={!selectedReportId}>Download saved report</Button>
            </Stack>
          </Paper>}
        </Box>
      </Container>

      <Dialog open={showOnboarding} onClose={() => { localStorage.setItem('food-tests-onboarded', 'true'); setShowOnboarding(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Food Tests Laboratory</DialogTitle>
        <DialogContent>
          <Typography>Prepare a clean tube, choose a test, follow the reagent order and conditions, then record the observed colour and inference. Use the water bath for Benedict’s tests and neutralise the hydrolysed sample before testing non-reducing sugars.</Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => { localStorage.setItem('food-tests-onboarded', 'true'); setShowOnboarding(false); }}>Start practical</Button></DialogActions>
      </Dialog>

      <Dialog open={showResult} onClose={() => setShowResult(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Composition inference</DialogTitle>
        <DialogContent><Typography>The unknown sample is {activeFood.name}. Compare your selected composition with the recorded test evidence in the notebook.</Typography></DialogContent>
        <DialogActions><Button onClick={() => setShowResult(false)}>Continue</Button></DialogActions>
      </Dialog>
      <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice('')} message={notice} />
    </Box>
  );
};

export default FoodTestsLab;